/**
 * 前端埋点工具类
 * 用于监控PV、UV、点击事件等用户行为数据
 */

import axios from "axios"
import md5 from "js-md5"
import { platformStore } from "@/store/index"
/**
 * 埋点配置
 */
const TRACKING_CONFIG = {
  // 后端埋点接口地址
  baseURL: "https://ad.qingcigame.com",

  // 接口路径
  endpoints: {
    pv: "/v1/web-purchase", // PV统计接口
    uv: "/v1/web-purchase", // UV统计接口
    click: "/v1/web-purchase", // 点击事件接口
    event: "/v1/web-purchase", // 通用事件接口
  },

  // 是否开启埋点
  enabled: true,

  // 是否在控制台打印日志
  debug: process.env.NODE_ENV === "development",
}

/**
 * 埋点工具类
 */
class TrackingManager {
  constructor() {
    this.sessionId = this.generateSessionId()
    this.userId = this.getUserId()
    this.visitId = this.generateVisitId()
    this.deviceInfo = this.getDeviceInfo()

    // 重试队列
    this.retryQueue = new Map() // 存储待重试的请求
    this.retryTimers = new Map() // 存储重试定时器

    // 初始化时记录UV
    this.initUV()
  }

  /**
   * 生成会话ID
   * @returns {string}
   */
  generateSessionId() {
    const existingSessionId = sessionStorage.getItem("tracking_session_id")
    if (existingSessionId) {
      return existingSessionId
    }

    const sessionId = `session_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`
    sessionStorage.setItem("tracking_session_id", sessionId)
    return sessionId
  }

  /**
   * 生成访问ID
   * @returns {string}
   */
  generateVisitId() {
    return `visit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * 获取用户ID（从localStorage或cookie中获取）
   * @returns {string|null}
   */
  getUserId() {
    // TODO: 根据实际业务逻辑获取用户ID
    return localStorage.getItem("user_id") || null
  }

  /**
   * 获取设备信息
   * @returns {object}
   */
  getDeviceInfo() {
    const ua = navigator.userAgent
    return {
      userAgent: ua,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      deviceType: this.getDeviceType(ua),
      browser: this.getBrowserInfo(ua),
      os: this.getOSInfo(ua),
    }
  }

  /**
   * 获取设备类型
   * @param {string} ua - User Agent
   * @returns {string}
   */
  getDeviceType(ua) {
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return "tablet"
    }
    if (
      /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        ua,
      )
    ) {
      return "mobile"
    }
    return "desktop"
  }

  /**
   * 获取浏览器信息
   * @param {string} ua - User Agent
   * @returns {object}
   */
  getBrowserInfo(ua) {
    let browser = "Unknown"
    let version = "Unknown"

    if (ua.indexOf("Firefox") > -1) {
      browser = "Firefox"
      version = ua.match(/Firefox\/(\d+\.\d+)/)?.[1] || "Unknown"
    } else if (ua.indexOf("Chrome") > -1) {
      browser = "Chrome"
      version = ua.match(/Chrome\/(\d+\.\d+)/)?.[1] || "Unknown"
    } else if (ua.indexOf("Safari") > -1) {
      browser = "Safari"
      version = ua.match(/Version\/(\d+\.\d+)/)?.[1] || "Unknown"
    } else if (ua.indexOf("Edge") > -1 || ua.indexOf("Edg") > -1) {
      browser = "Edge"
      version = ua.match(/Edg\/(\d+\.\d+)/)?.[1] || "Unknown"
    } else if (ua.indexOf("MSIE") > -1 || ua.indexOf("Trident") > -1) {
      browser = "IE"
      version = ua.match(/(?:MSIE |rv:)(\d+\.\d+)/)?.[1] || "Unknown"
    }

    return { browser, version }
  }

  /**
   * 获取操作系统信息
   * @param {string} ua - User Agent
   * @returns {string}
   */
  getOSInfo(ua) {
    if (ua.indexOf("Win") > -1) return "Windows"
    if (
      ua.indexOf("iOS") > -1 ||
      ua.indexOf("iPhone") > -1 ||
      ua.indexOf("iPad") > -1
    )
      return "iOS"
    if (ua.indexOf("Mac") > -1) return "MacOS"
    if (ua.indexOf("Linux") > -1) return "Linux"
    if (ua.indexOf("Android") > -1) return "Android"
    return "Unknown"
  }

  /**
   * 计算重试延迟时间（指数退避）
   * @param {number} retryCount - 重试次数
   * @returns {number} 延迟时间（毫秒）
   */
  calculateRetryDelay(retryCount) {
    // 基础延迟：4秒、8秒、16秒、32秒、60秒
    const baseDelay = 4000 // 4秒
    const maxDelay = 60000 // 60秒

    // 指数退避：4 * (2^retryCount) 秒
    const delay = baseDelay * Math.pow(2, retryCount)

    // 限制最大延迟为60秒
    return Math.min(delay, maxDelay)
  }

  /**
   * 生成请求的唯一ID
   * @returns {string}
   */
  generateRequestId() {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * 添加到重试队列
   * @param {string} requestId - 请求ID
   * @param {string} endpoint - 接口路径
   * @param {object} trackingData - 埋点数据
   * @param {number} retryCount - 当前重试次数
   */
  addToRetryQueue(requestId, endpoint, trackingData, retryCount = 0) {
    // 检查是否已经超过最大重试时间
    const maxRetryCount = 5 // 4秒、8秒、16秒、32秒、60秒，共5次

    if (retryCount >= maxRetryCount) {
      if (TRACKING_CONFIG.debug) {
        console.warn(
          "[Tracking] 重试次数已达上限，放弃重试:",
          endpoint,
          trackingData,
        )
      }
      // 清理重试数据
      this.retryQueue.delete(requestId)
      this.retryTimers.delete(requestId)
      return
    }

    // 计算延迟时间
    const delay = this.calculateRetryDelay(retryCount)

    if (TRACKING_CONFIG.debug) {
      console.log(
        `[Tracking] 将在 ${delay / 1000} 秒后进行第 ${retryCount + 1} 次重试:`,
        endpoint,
      )
    }

    // 保存到重试队列
    this.retryQueue.set(requestId, {
      endpoint,
      trackingData,
      retryCount: retryCount + 1,
      nextRetryTime: Date.now() + delay,
    })

    // 设置延迟重试
    const timer = setTimeout(() => {
      this.retryRequest(requestId)
    }, delay)

    this.retryTimers.set(requestId, timer)
  }

  /**
   * 执行重试请求
   * @param {string} requestId - 请求ID
   */
  async retryRequest(requestId) {
    const retryData = this.retryQueue.get(requestId)

    if (!retryData) {
      return
    }

    const { endpoint, trackingData, retryCount } = retryData

    if (TRACKING_CONFIG.debug) {
      console.log(`[Tracking] 正在执行第 ${retryCount} 次重试:`, endpoint)
    }

    try {
      const response = await axios.post(
        `${TRACKING_CONFIG.baseURL}${endpoint}`,
        trackingData,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 5000, // 5秒超时
        },
      )

      if (TRACKING_CONFIG.debug) {
        console.log(`[Tracking] 第 ${retryCount} 次重试成功:`, response.data)
      }

      // 重试成功，清理重试数据
      this.retryQueue.delete(requestId)
      this.retryTimers.delete(requestId)

      return response.data
    } catch (error) {
      if (TRACKING_CONFIG.debug) {
        console.error(`[Tracking] 第 ${retryCount} 次重试失败:`, error.message)
      }

      // 清理当前定时器
      this.retryTimers.delete(requestId)

      // 继续重试
      this.addToRetryQueue(requestId, endpoint, trackingData, retryCount)
    }
  }

  /**
   * 清除指定请求的重试
   * @param {string} requestId - 请求ID
   */
  clearRetry(requestId) {
    // 清除定时器
    const timer = this.retryTimers.get(requestId)
    if (timer) {
      clearTimeout(timer)
      this.retryTimers.delete(requestId)
    }

    // 清除队列数据
    this.retryQueue.delete(requestId)
  }

  /**
   * 清除所有重试
   */
  clearAllRetries() {
    // 清除所有定时器
    this.retryTimers.forEach((timer) => clearTimeout(timer))
    this.retryTimers.clear()

    // 清除队列
    this.retryQueue.clear()

    if (TRACKING_CONFIG.debug) {
      console.log("[Tracking] 已清除所有重试任务")
    }
  }

  /**
   * 生成随机时间偏移（-600秒到+600秒之间）
   * @returns {number} 偏移的毫秒数
   */
  generateRandomOffset() {
    // 生成 -600 到 +600 之间的随机秒数
    const randomSeconds = Math.floor(Math.random() * 1201) - 600
    return randomSeconds * 1000
  }

  /**
   * 生成时间戳（当前时间前后600秒内）
   * @returns {number} Unix时间戳（秒）
   */
  generateTimestamp() {
    const now = Date.now()
    const offset = this.generateRandomOffset()
    const timestamp = Math.floor((now + offset) / 1000)
    return timestamp
  }

  /**
   * 生成事件时间（ISO 8601格式）
   * @returns {string} 格式化的事件时间，例如：2026-01-26T10:20:30+08:00
   */
  generateEventAt() {
    const now = new Date()

    // 获取时区偏移（分钟）
    const timezoneOffset = -now.getTimezoneOffset()
    const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60)
    const offsetMinutes = Math.abs(timezoneOffset) % 60
    const offsetSign = timezoneOffset >= 0 ? "+" : "-"

    // 格式化时区字符串，例如：+08:00
    const timezoneStr = `${offsetSign}${String(offsetHours).padStart(
      2,
      "0",
    )}:${String(offsetMinutes).padStart(2, "0")}`

    // 格式化日期时间部分
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, "0")
    const day = String(now.getDate()).padStart(2, "0")
    const hours = String(now.getHours()).padStart(2, "0")
    const minutes = String(now.getMinutes()).padStart(2, "0")
    const seconds = String(now.getSeconds()).padStart(2, "0")

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${timezoneStr}`
  }

  /**
   * 生成签名
   * @param {number} timestamp - 时间戳
   * @returns {string} MD5签名（小写）
   */
  generateSign(timestamp) {
    const secret = "a3f9d1c07b2e4e6f8c1a9d4f0b7e2c5a"
    const signStr = `${secret}${timestamp}`
    return md5(signStr).toLowerCase()
  }

  /**
   * 发送埋点数据到后端（带重试机制）
   * @param {string} endpoint - 接口路径
   * @param {object} data - 埋点数据
   * @returns {Promise}
   */
  async sendTrackingData(endpoint, data) {
    if (!TRACKING_CONFIG.enabled) {
      if (TRACKING_CONFIG.debug) {
        console.log("[Tracking] 埋点已禁用")
      }
      return
    }

    // 生成时间戳和签名
    const timestamp = this.generateTimestamp()
    const sign = this.generateSign(timestamp)
    const eventAt = this.generateEventAt()
    const store = platformStore()
    const ua = navigator.userAgent
    const trackingData = {
      ...data,
      sign: sign,
      time_stamp: timestamp,
      event_at: eventAt,
      url: window.location.href,
      account: store.mobile,
      user_id: store.userId,
      login_type: store.loginWay,
      area: store.nowType == "zhcn" ? 1 : 2,
      os: this.getOSInfo(ua),
    }

    if (TRACKING_CONFIG.debug) {
      console.log("[Tracking] 发送埋点数据:", endpoint, trackingData)
    }

    // 检查后端接口地址
    if (!TRACKING_CONFIG.baseURL) {
      if (TRACKING_CONFIG.debug) {
        console.warn("[Tracking] 后端接口地址未配置，埋点数据未发送")
      }
      return
    }

    // 生成请求ID
    const requestId = this.generateRequestId()

    try {
      const response = await axios.post(
        `${TRACKING_CONFIG.baseURL}${endpoint}`,
        trackingData,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 5000, // 5秒超时
        },
      )

      if (TRACKING_CONFIG.debug) {
        console.log("[Tracking] 埋点数据发送成功:", response.data)
      }

      return response.data
    } catch (error) {
      if (TRACKING_CONFIG.debug) {
        console.error("[Tracking] 埋点数据发送失败:", error.message)
      }

      // 添加到重试队列
      this.addToRetryQueue(requestId, endpoint, trackingData, 0)

      // 埋点失败不影响业务流程
      return null
    }
  }

  /**
   * 初始化UV统计
   */
  initUV() {
    const lastVisitDate = localStorage.getItem("tracking_last_visit_date")
    const today = new Date().toDateString()

    // 如果是新的一天，记录UV
    if (lastVisitDate !== today) {
      this.trackUV()
      localStorage.setItem("tracking_last_visit_date", today)
    }
  }

  /**
   * 记录PV（页面浏览量）
   */
  trackPV(params = {}) {
    const store = platformStore()
    const {
      page = "",
      source = "",
      role_id = "",
      server_id = "",
      role_name = "",
      game_id = store.gameId,
    } = params

    this.sendTrackingData(TRACKING_CONFIG.endpoints.pv, {
      event: "view",
      page,
      source,
      role_id,
      server_id,
      role_name,
      game_id,
    })
  }

  /**
   * 记录UV（独立访客）
   * @param {object} params - 用户参数
   * @param {object} params.extraData - 额外数据
   */
  trackUV(params = {}) {
    const { extraData = {} } = params

    this.sendTrackingData(TRACKING_CONFIG.endpoints.uv, {
      eventType: "uv",
      extraData,
    })
  }

  /**
   * 记录点击事件
   */
  trackClick(params = {}) {
    const store = platformStore()
    const {
      game_id = store.gameId,
      button = "",
      role_id = "",
      server_id = "",
      role_name = "",
      click_name = "",
      click_id = "",
      price = 0,
      page = "",
      point = 0,
    } = params

    this.sendTrackingData(TRACKING_CONFIG.endpoints.click, {
      event: "click",
      game_id,
      button,
      role_id,
      server_id,
      role_name,
      click_name,
      click_id,
      price,
      page,
      point,
    })
  }

  /**
   * 记录通用事件
   */
  trackEvent(params = {}) {
    const store = platformStore()
    const { status, game_id = store.gameId } = params

    this.sendTrackingData(TRACKING_CONFIG.endpoints.event, {
      event: "login",
      status,
      game_id,
    })
  }

  /**
   * 设置用户ID
   * @param {string} userId - 用户ID
   */
  setUserId(userId) {
    this.userId = userId
    localStorage.setItem("user_id", userId)
  }

  /**
   * 清除用户ID（用户登出时调用）
   */
  clearUserId() {
    this.userId = null
    localStorage.removeItem("user_id")
  }

  /**
   * 获取重试队列状态（用于调试）
   * @returns {object}
   */
  getRetryStatus() {
    const status = {
      queueSize: this.retryQueue.size,
      pendingRetries: [],
    }

    this.retryQueue.forEach((retryData, requestId) => {
      status.pendingRetries.push({
        requestId,
        endpoint: retryData.endpoint,
        retryCount: retryData.retryCount,
        nextRetryTime: new Date(retryData.nextRetryTime).toLocaleString(),
        remainingTime: Math.max(0, retryData.nextRetryTime - Date.now()),
      })
    })

    return status
  }
}

// 创建单例实例
const trackingManager = new TrackingManager()

/**
 * 导出便捷方法
 */
export const tracking = {
  /**
   * 记录PV
   */
  trackPV: (params) => trackingManager.trackPV(params),

  /**
   * 记录UV
   */
  trackUV: (params) => trackingManager.trackUV(params),

  /**
   * 记录点击事件
   */
  trackClick: (params) => trackingManager.trackClick(params),

  /**
   * 记录通用事件
   */
  trackEvent: (params) => trackingManager.trackEvent(params),

  /**
   * 设置用户ID
   */
  setUserId: (userId) => trackingManager.setUserId(userId),

  /**
   * 清除用户ID
   */
  clearUserId: () => trackingManager.clearUserId(),

  /**
   * 清除所有重试任务
   */
  clearAllRetries: () => trackingManager.clearAllRetries(),

  /**
   * 获取重试队列状态（调试用）
   */
  getRetryStatus: () => trackingManager.getRetryStatus(),
}

export default tracking
